// edit-unit.component.ts
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { UnitService } from 'app/services/units/unit.service';

@Component({
  selector: 'app-edit-unit',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './edit-unit.component.html',
  styleUrls: ['./edit-unit.component.scss']
})
export class EditUnitComponent implements OnInit {
  unitForm!: FormGroup;
  submitted = false;
  isLoading = true;
  loadingMessage = 'جاري تحميل بيانات الوحدة...';
  currentUnitId: number = 0;
  isUpdating = false;

  
  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private unitService:UnitService
  ) {}

  ngOnInit() {
    this.initializeForm();
    this.loadUnitData();
  }

  private initializeForm() {
    this.unitForm = this.fb.group({
      code: ['', [ Validators.minLength(1), Validators.maxLength(10)]],
      type: [''],
      arName: ['', [Validators.required, Validators.minLength(2)]],
      enName: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  private loadUnitData(): void {
    const unitId = this.route.snapshot.paramMap.get('id');
    
    if (!unitId || isNaN(+unitId)) {
      console.error('❌ Invalid unit ID:', unitId);
      alert('❌ معرّف الوحدة غير صالح');
      this.router.navigate(['/admin/units']);
      return;
    }

    this.currentUnitId = +unitId;
    console.log('🟡 Loading unit with ID:', this.currentUnitId);

    this.unitService.getUnitById(this.currentUnitId).subscribe({
      next: (response) => {
        console.log('✅ Unit data loaded:', response);
        
        if (response.success) {
          const unit = response;
          this.patchUnitData(unit);
          this.isLoading = false;
        } else {
          console.error('❌ No unit data found');
          alert('❌ لم يتم العثور على بيانات الوحدة');
          this.router.navigate(['/admin/units']);
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error loading unit:', error);
        this.isLoading = false;
        this.handleLoadError(error);
      }
    });
    this.isLoading = false;

  }

  private patchUnitData(unit: any): void {
    console.log('🟡 Patching unit data:', unit);
    
    this.unitForm.patchValue({
      code: unit.code || '',
      type: unit.type || 0,
      arName: unit.ar_name || '',
      enName: unit.en_name || ''
    });

    console.log('✅ Unit data patched successfully');
  }

  // الدوال المساعدة للوصول إلى عناصر الفورم
  get f(): { [key: string]: AbstractControl } {
    return this.unitForm.controls;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.unitForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  getFieldError(fieldName: string): string {
    const field = this.unitForm.get(fieldName);
    if (field?.errors) {
      if (field.errors['required']) {
        return 'هذا الحقل مطلوب';
      }
      if (field.errors['minlength']) {
        return `الحد الأدنى للحروف هو ${field.errors['minlength'].requiredLength}`;
      }
      if (field.errors['maxlength']) {
        return `الحد الأقصى للحروف هو ${field.errors['maxlength'].requiredLength}`;
      }
      if (field.errors['min']) {
        return `القيمة يجب أن تكون ${field.errors['min'].min} أو أكثر`;
      }
    }
    return '';
  }

  // تحديث الوحدة
  onSubmit(): void {
     this.submitted = true;

    if (!this.unitForm.valid) {
      this.markFormGroupTouched(this.unitForm);
      alert('❌ يوجد أخطاء في النموذج. راجع الحقول المطلوبة.');
     this.submitted = false;

      return;
    }

    if (!this.hasFormChanged()) {
      alert('⚠️ لم تقم بإجراء أي تغييرات على البيانات');
     this.submitted = false;
      return;
    }

    this.updateUnit();
  }

  private hasFormChanged(): boolean {
    // في تطبيق حقيقي، يمكنك مقارنة القيم الحالية مع القيم الأصلية
    // هنا نعتبر أن هناك تغيير إذا كان النموذج touched
    return this.unitForm.dirty;
  }

  private updateUnit(): void {
    this.isUpdating = true;
    this.loadingMessage = 'جاري تحديث الوحدة...';
    const unitData = this.prepareUnitData();

    console.log('🟡 Updating unit with data:', unitData);

    this.unitService.updateUnit(this.currentUnitId, unitData).subscribe({
      next: (response) => {
        console.log('✅ Unit updated successfully:', response);
        this.isUpdating = false;
        
        if (response.success) {
          alert('✅ تم تحديث الوحدة بنجاح!');
          this.router.navigate(['/admin/units']);
        } else {
          alert('❌ ' + ( 'حدث خطأ أثناء تحديث الوحدة'));
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error updating unit:', error);
        this.isUpdating = false;
        this.handleError(error);
      }
    });
  }
    
   private prepareUnitData(): any {
    const formValue = this.unitForm.value;
    
    return {
      code: formValue.code.trim(),
      type: formValue.type,
      ar_name: formValue.arName.trim(),
      en_name: formValue.enName.trim()
    };
  }

  

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }

  private handleLoadError(error: HttpErrorResponse): void {
    let errorMessage = '❌ حدث خطأ في تحميل بيانات الوحدة';
    
    if (error.status === 404) {
      errorMessage = '❌ الوحدة غير موجودة';
    } else if (error.status === 0) {
      errorMessage = '❌ تعذر الاتصال بالخادم';
    }
    
    alert(errorMessage);
    this.router.navigate(['/admin/units']);
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = '❌ حدث خطأ أثناء تحديث الوحدة';
    
    if (error.error && error.error.errors) {
      const validationErrors = error.error.errors;
      const errorMessages = [];
      
      for (const field in validationErrors) {
        if (validationErrors.hasOwnProperty(field)) {
          errorMessages.push(...validationErrors[field]);
        }
      }
      
      if (errorMessages.length > 0) {
        errorMessage = '❌ ' + errorMessages.join('\n');
      }
    } else if (error.error && error.error.message) {
      errorMessage = '❌ ' + error.error.message;
    } else if (error.status === 404) {
      errorMessage = '❌ الوحدة غير موجودة';
    } else if (error.status === 409) {
      errorMessage = '❌ كود الوحدة موجود مسبقاً';
    }
    
    alert(errorMessage);
  }

  onReset(): void {
    if (confirm('هل تريد إعادة تعيين التغييرات؟')) {
      this.loadUnitData();
      this.submitted = false;
    }
  }

  onCancel(): void {
    if (this.unitForm.dirty) {
      if (confirm('هل تريد إلغاء التغييرات؟ سيتم فقدان جميع التعديلات.')) {
        this.router.navigate(['/admin/units']);
      }
    } else {
      this.router.navigate(['/admin/units']);
    }
  }

  // دالة مساعدة لإنشاء كود تلقائي
  generateCode(): void {
    const name = this.unitForm.get('enName')?.value;
    if (name && name.length >= 2) {
      const code = name.substring(0, 3).toUpperCase();
      this.unitForm.patchValue({ code });
    }
  }


  // دالة للتحقق إذا كان النموذج محمل
  isFormLoaded(): boolean {
    return !this.isLoading && this.currentUnitId > 0;
  }
}